import { hasRoleSlug } from '@utils/user';
import { useMemo } from 'react';

import { useCurrentUserContextRoles } from '@/hooks/usePermissions';

/**
 * Canonical slug for the global system administrator role.
 * Source of truth: pkg/auth/role_templates.go (RoleTemplateSystemAdmin).
 */
const SYSTEM_ADMIN_ROLE_SLUG = 'system-admin';

/**
 * Custom hook to determine if the current user is a global system administrator.
 *
 * System admins hold the `system-admin` role at global scope (no organization or
 * program binding), so the role is always present in the context-filtered roles.
 *
 * Note: this is a convenience gate for the UI only. Authorization is always
 * enforced server-side by the Casbin engine.
 *
 * @returns {boolean} true if the user holds the system-admin role, false otherwise
 */
export const useIsSystemAdmin = (): boolean => {
  const { roles } = useCurrentUserContextRoles();

  return useMemo(() => hasRoleSlug(roles, SYSTEM_ADMIN_ROLE_SLUG), [roles]);
};
